import { createEvent, restore, sample } from 'effector'
import { fx } from '../../../../../libs'
import { parseDecimal } from '../../../../../libs/utils'
import { TransferForm, TransferRequest } from './types'
import { ErrorSubmit } from '../types'

const buildBody = (data: TransferForm & { toWallet: string }) => ({
  fromWallet: data.fromWallet,
  toWallet: data.toWallet,
  sentAmount: parseDecimal(data.sentAmount),
  sentCurrency: data.sentCurrency,
  receivedAmount: parseDecimal(data.receivedAmount),
  receivedCurrency: data.receivedCurrency,
  sentCommission: parseDecimal(data.sentCommission),
  sentCommissionCurrency: data.sentCommissionCurrency,
  receivedCommission: parseDecimal(data.receivedCommission),
  receivedCommissionCurrency: data.receivedCommissionCurrency,
  sentExchangeRate: parseDecimal(data.sentExchangeRate),
  receivedExchangeRate: parseDecimal(data.receivedExchangeRate),
  comment: data.comment,
  inProgress: data.inProgress,
  dateTime: data.dateTime,
})

export const addTransferMounted = createEvent<
  TransferForm & { toWallet: string }
>()

export const editTransferMounted = createEvent<
  TransferForm & { toWallet: string }
>()

export const getTransferByIdMounted = createEvent<{ id: string }>()

export const setTransferForm = createEvent<TransferForm>()

export const deleteTransferMounted = createEvent<{ id: string }>()

export const setError = createEvent<ErrorSubmit>()

const $requestedTransferId = restore(
  getTransferByIdMounted.map(({ id }) => String(id)),
  '',
)

export const $transferForm = restore(setTransferForm, {
  id: '',
  sentAmount: '',
  sentCurrency: 'USD',
  sentCommission: '0',
  sentCommissionCurrency: '',
  sentExchangeRate: '0',
  receivedAmount: '',
  receivedCurrency: '',
  receivedCommission: '0',
  receivedCommissionCurrency: '',
  receivedExchangeRate: '0',
  comment: '',
  fromWallet: '',
  toWalletId: '',
  inProgress: false,
  dateTime: '',
})

export const $error = restore(setError, {
  message: '',
  code: 0,
})

export const addTransfer = fx<ReturnType<typeof buildBody>, void>({
  method: 'POST',
  url: `/finance/transfer`,
})

export const editTransferPayment = fx<ReturnType<typeof buildBody>, void>({
  method: 'PUT',
  url: `/finance/transfer/{id}`,
})

export const getTransferById = fx<{ id: string }, TransferRequest>({
  method: 'GET',
  url: `/finance/transfer/{id}`,
})

export const deleteTransfer = fx<TransferForm, void>({
  method: 'DELETE',
  url: `/finance/transfer/{id}`,
})

sample({
  clock: deleteTransferMounted,
  fn: ({ id }) => {
    return {
      url: `/finance/transfer/${id}`,
    }
  },
  target: deleteTransfer,
})

sample({
  clock: addTransferMounted,
  fn: (transferData) => ({ body: buildBody(transferData) }),
  target: addTransfer,
})

sample({
  clock: editTransferMounted,
  fn: (transferData) => ({
    url: `/finance/transfer/${transferData.id}`,
    body: buildBody(transferData),
  }),
  target: editTransferPayment,
})

sample({
  clock: getTransferByIdMounted,
  fn: ({ id }) => {
    return {
      url: `/finance/transfer/${id}`,
    }
  },
  target: getTransferById,
})

sample({
  clock: [addTransfer.failData],
  fn: (failData) => {
    return { message: failData.body.message, code: failData.body.code }
  },
  target: [setError],
})

sample({
  source: $requestedTransferId,
  clock: getTransferById.doneData,
  filter: (requestedTransferId, doneData) =>
    requestedTransferId === String(doneData.body.id),
  fn: (_, doneData) => ({
    id: String(doneData.body.id),
    sentAmount: doneData.body.sentAmount,
    sentCurrency: doneData.body.sentCurrency,
    sentCommission: String(doneData.body.sentCommission),
    sentCommissionCurrency: String(doneData.body.sentCommissionCurrency),
    sentExchangeRate: String(doneData.body.sentExchangeRate),
    receivedAmount: String(doneData.body.receivedAmount),
    receivedCurrency: String(String(doneData.body.receivedCurrency)),
    receivedCommission: String(doneData.body.receivedCommission),
    receivedCommissionCurrency: String(doneData.body.receivedCommissionCurrency),
    receivedExchangeRate: String(doneData.body.receivedExchangeRate),
    comment: doneData.body.comment,
    fromWallet: String(doneData.body.fromWalletId),
    toWalletId: String(doneData.body.toWalletId),
    inProgress: doneData.body.inProgress,
    dateTime: doneData.body.dateTime,
  }),
  target: [setTransferForm],
})
